<script setup>
import { onMounted, shallowRef } from "vue";
import { runs as runsApi } from "~/api";
import EmptyState from "~/components/EmptyState.vue";
import GlLoadingIcon from "~/components/gitlab/GlLoadingIcon.vue";
import GlToggle from "~/components/gitlab/GlToggle.vue";

const { isAdmin, runId } = defineProps({
  isAdmin: { default: false, type: Boolean },
  runId: { required: true, type: String }
});

// Level → GitLab semantic color tokens (mirrors GitLab's own log viewer colors)
const LEVEL_CLASSES = {
  DEBUG: "text-[var(--gl-text-color-disabled)]",
  ERROR: "text-[var(--gl-text-color-danger)]",
  FATAL: "text-[var(--gl-text-color-danger)] font-bold",
  INFO: "text-[var(--gl-status-info-text-color)]",
  UNKNOWN: "text-[var(--gl-text-color-disabled)]",
  WARN: "text-[var(--gl-text-color-warning)]"
};

const showDebug = shallowRef(false);
const loading = shallowRef(true);
const logs = shallowRef([]);

async function fetchLogs(level = 1) {
  const data = await runsApi.logs({ query: { id: runId, level } });
  logs.value = data;
  loading.value = false;
}
async function handleToggle(enabled) {
  await fetchLogs(enabled ? 0 : 1);
  showDebug.value = enabled;
}

function levelClass(level) {
  return LEVEL_CLASSES[level] ?? "text-[var(--gl-text-color-disabled)]";
}

onMounted(() => fetchLogs(showDebug.value ? 0 : 1));
</script>

<template>
  <div class="overflow-hidden rounded-lg border border-[var(--gl-border-color-default)]">
    <!-- Section header -->
    <div
      class="flex items-center justify-between border-b border-[var(--gl-border-color-default)] bg-[var(--gl-background-color-subtle)] px-5 py-3"
    >
      <span class="text-xs font-semibold uppercase tracking-wide text-[var(--gl-text-color-subtle)]">
        Update logs
      </span>

      <GlToggle
        v-if="isAdmin"
        :model-value="showDebug"
        label="Debug"
        label-position="left"
        @update:model-value="handleToggle($event)"
      />
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex justify-center py-12">
      <GlLoadingIcon size="lg" />
    </div>

    <!-- Empty state -->
    <EmptyState
      v-else-if="logs.length === 0"
      title="No log entries found"
      description="Log entries may not be available if the retention period has expired or latest run is still in progress."
    />

    <!-- Log lines -->
    <div v-else class="overflow-x-auto">
      <table class="w-full font-mono text-xs">
        <tbody>
          <tr
            v-for="(entry, index) in logs"
            :key="index"
            class="transition-colors hover:bg-[var(--gl-background-color-subtle)]"
          >
            <!-- Timestamp — non-selectable, muted -->
            <td class="select-none whitespace-nowrap py-0.5 pl-4 pr-2 text-[var(--gl-text-color-disabled)]">
              {{ entry.timestamp }}
            </td>
            <!-- Level — colored by severity -->
            <td class="whitespace-nowrap py-0.5 pr-4">
              <span :class="['font-semibold uppercase', levelClass(entry.level)]">{{ entry.level }}</span>
            </td>
            <!-- Message -->
            <td class="w-full whitespace-pre-wrap py-0.5 pr-4 text-[var(--gl-text-color-default)]">
              {{ entry.message }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
